import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { RequestError, useApi } from "../../services";
import { BarragemData, useUserContext } from "../../context";
import { AddManchaModal, ConfirmModal, ErrorModal, LoadingModal } from "../modals";
import { Add, Delete, OpenInNew } from "@mui/icons-material";
import { useWindowSize } from "../../core";
import { useNavigate } from "react-router-dom";
import { CompanyData } from "../../pages";
import { PolyMancha } from "../barragem";

export interface TableManchaProps {
  title?: string;
  companyFilter?: CompanyData;
  isAddNew?: boolean;
  notEditable?: boolean;
  onRemove?: (mancha: PolyMancha | RequestError) => void;
}

export const TableManchas: React.FC<TableManchaProps> = ({ title, companyFilter, isAddNew, onRemove }) => {
  const api = useApi();
  const user = useUserContext();
  const { height, width } = useWindowSize();

  const sizeRef = useRef<HTMLDivElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState(false);
  const [errorAdd, setErrorAdd] = useState<RequestError>();
  const [openNewModal, setOpenNewModal] = useState(false);
  const [barragens, setBrragens] = useState<BarragemData[] | RequestError>([]);
  const [manchas, setManchas] = useState<{ [key: string]: (PolyMancha | RequestError)[]; }>({});
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [confirmRemove, setConfirmRemove] = useState<PolyMancha>();
  const [loading, setLoading] = useState(false);

  // Lista de Barragens
  useEffect(() => {
    const getData = async () => {
      if (!user || "error" in user) return;

      setLoading(true);
      const data = await api.barragem.getBarragens();
      setLoading(false);
      setBrragens(data);
    };
    getData();

  }, [user, state]);

  // Lista de Manchas por Barragem
  useEffect(() => {
    const getData = async () => {
      if (!barragens || "error" in barragens) return;


      setLoading(true);
      const newManchas: { [key: string]: (PolyMancha | RequestError)[]; } = {};
      for (const barragem of barragens) {
        newManchas[barragem.id] = [];

        for (const id of barragem.manchas ?? []) {
          const data = await api.manchas.getTable(id);
          newManchas[barragem.id].push(data);
        }
      }

      setLoading(false);
      setManchas(newManchas);
    };
    getData();
  }, [barragens]);

  // Ajustar o tamanho da tabela
  useEffect(() => {
    setTimeout(() => {
      let heightTable = height;
      let widthTable = width;
      if (sizeRef.current) {
        const size = sizeRef.current.getBoundingClientRect();
        widthTable = size.width;
        heightTable = size.height;
      }
      if (divRef.current) {
        const size = divRef.current.getBoundingClientRect();
        heightTable -= size.height;
      }
      if (filterRef.current) {
        const size = filterRef.current.getBoundingClientRect();
        heightTable -= size.height;
      }
      if (titleRef.current) {
        const size = titleRef.current.getBoundingClientRect();
        heightTable -= size.height;
      }
      setSize({ width: widthTable, height: heightTable });
    }, 100);

  }, [sizeRef, divRef, filterRef, titleRef, height, width]);

  const handleAdd = async (value?: PolyMancha) => {
    if (!value) return;
    setState(!state);
    setOpenNewModal(false);
  };

  const handleRemove = (mancha: PolyMancha) => {
    if (!mancha.properties?.id) return;

    setLoading(true);

    api.manchas.remove(mancha.properties.id).then(result => {
      setLoading(false);

      if ("error" in result) {
        setErrorAdd(result);
        return;
      }

      setState(!state);
      onRemove?.(mancha);
    });

  };

  if ("error" in barragens) return <ErrorModal {...barragens} />;

  return <>

    {/* Leading */}
    {loading && <LoadingModal open={loading} />}

    {/* Erro */}
    {errorAdd &&
      <Snackbar
        open={Boolean(errorAdd)}
        autoHideDuration={4500}
        onClose={() => setErrorAdd(undefined)}
        message="Note archived"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setErrorAdd(undefined)} severity="error" sx={{ width: "100%" }}>
          {errorAdd.error + ":" + errorAdd.message}
        </Alert>
      </Snackbar>
    }

    {confirmRemove && <ConfirmModal
      open={Boolean(confirmRemove)}
      title="Remover Mancha de Inundação"
      message={`Tem certeza que deseja remover a mancha "${confirmRemove.properties?.nome ?? "Sem Nome"}"? Esta ação não pode ser desfeita.`}
      onCancel={() => setConfirmRemove(undefined)}
      onConfirm={() => {
        handleRemove(confirmRemove);
        setConfirmRemove(undefined);
      }}
    />}

    {/* Modal para o nova mancha */}
    {openNewModal &&
      <AddManchaModal
        open={openNewModal}
        onAdd={handleAdd}
        onCancel={() => setOpenNewModal(false)}
      />
    }

    {/* Tabela com os dados */}
    <Box
      ref={sizeRef}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}>
      {/* Título e nova mancha */}
      <Box
        ref={titleRef}
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography fontWeight={"bold"} fontSize={24}>
          {title ?? "Manchas de Inundação"}
        </Typography>

        {isAddNew && user && !("error" in user) && user.admin &&
          <Button
            variant="contained"
            onClick={() => setOpenNewModal(true)}
            endIcon={<Add />}
          >
            Nova Mancha de Inundação
          </Button>
        }
      </Box>

      {/* Tabela com as manchas */}
      <TableContainer sx={{ flexGrow: 1, width: size.width, height: size.height, overflow: "auto" }}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Barragem</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Manchas</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Acessar</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>

            {barragens.filter(barragem => companyFilter ? (barragem.idEmpreendimento === companyFilter.id) : true).map(barragem =>
              <TableRow key={barragem.id} hover>
                {/* Nome */}
                <TableCell align="center">{barragem.nome}</TableCell>

                {/* Dados das manchas */}
                <RowMancha
                  key={"M" + barragem.id}
                  manchas={manchas[barragem.id] ?? []}
                  onRemove={(mancha) => setConfirmRemove(mancha)}
                />

                {/* Remover ou acessar a mancha */}
                <RowAccess
                  key={"R" + barragem.id}
                  manchas={manchas[barragem.id] ?? []}
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  </>;
};

const RowMancha: React.FC<{
  manchas: (PolyMancha | RequestError)[];
  onRemove: (mancha: PolyMancha) => void;
}> = ({ manchas, onRemove }) => {

  return <TableCell>

    {/* Lista de manchas */}
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "5px",
      }}
    >
      {manchas.map((mancha, idx) => (
        "error" in mancha ?
          <Typography key={idx} color="error">
            {mancha.error + ": " + mancha.message}
          </Typography>
          :
          <Box key={idx} sx={{ alignItems: "center", display: "flex", gap: "5px", flexDirection: "row" }}>
            <Typography fontWeight={"bold"}>
              {mancha.properties?.nome ?? "Sem Nome"}
            </Typography>

            <Tooltip title='Remover Mancha'>
              <IconButton size="small" onClick={() => onRemove(mancha)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
      ))}
    </Box>
  </TableCell>;
};

const RowAccess: React.FC<{
  manchas: (PolyMancha | RequestError)[];
}> = ({ manchas }) => {
  const navigate = useNavigate();

  return <TableCell sx={{ width: "100px" }} align="center">

    {manchas.filter(mancha => !("error" in mancha)).length > 0 &&
      <Tooltip title='Ir para as manchas da barragem'>
        <IconButton size="small" onClick={() => navigate(`/app/manchas/${(manchas.filter(mancha => !("error" in mancha)) as PolyMancha[])[0].properties?.idBarragem ?? -1}`)}>
          <OpenInNew fontSize="small" />
        </IconButton>
      </Tooltip>}

  </TableCell>;
};

